import endpoints from '@/constants/endpoints';
import axiosV2 from '@/data/api/axiosv2';
import { ITEMS_TABLE_PAGE_LIMIT } from '@/utils/constants';
import {
  CreateSellerRequest,
  CreateSellerResponse,
  SellerAddressRequest,
  SellerAddressResponse,
  SellerContactDataResponse,
  SellerDeliveryDayResponse,
  SellerDetailsResponse,
  SellerFiscalDataResponse,
  SellerSettingsResponse,
  SellersResponse,
  UpdateSellerContactDataRequest,
  UpdateSellerDeliveryDetailsRequest,
  UpdateSellerDeliverySchedulesRequest,
  UpdateSellerDetailsRequest,
  UpdateSellerFiscalDataRequest,
} from '../model/sellers/SellersDto';

export const createSeller = async (
  createSellerRequest: CreateSellerRequest
): Promise<CreateSellerResponse> => {
  const endpoint = endpoints.seller.create;
  const response = await axiosV2.post<CreateSellerResponse>(
    endpoint,
    createSellerRequest
  );
  return response;
};

export const getSellers = async (args: {
  pageNumber?: number;
  searchTerm?: string;
  limit?: number;
}): Promise<SellersResponse> => {
  const endpoint = endpoints.seller.getSellers;

  const response = await axiosV2.get<SellersResponse>(endpoint, {
    params: {
      limit: args.limit ?? ITEMS_TABLE_PAGE_LIMIT,
      ...(args.pageNumber && { page: `${args.pageNumber}` }),
      ...(args.searchTerm && { filter: `${args.searchTerm}` }),
    },
  });

  return response;
};

export const getSellerDetails = async (args: {
  sellerId: string;
}): Promise<SellerDetailsResponse> => {
  const endpoint = endpoints.seller.getDetails.replace(
    ':sellerId',
    args.sellerId
  );
  return await axiosV2.get<SellerDetailsResponse>(endpoint);
};

export const updateSellerDetails = async (args: {
  sellerId: string;
  updateSellerDetailsRequest: UpdateSellerDetailsRequest;
}) => {
  const endpoint = endpoints.seller.updateDetails.replace(
    ':sellerId',
    args.sellerId
  );
  await axiosV2.patch(endpoint, args.updateSellerDetailsRequest);
};

export const getSellerContactData = async (args: {
  sellerId: string;
}): Promise<SellerContactDataResponse> => {
  const endpoint = endpoints.seller.contactData.replace(
    ':sellerId',
    args.sellerId
  );
  return await axiosV2.get<SellerContactDataResponse>(endpoint);
};

export const updateSellercontactData = async (args: {
  sellerId: string;
  updateSellerContactDataRequest: UpdateSellerContactDataRequest;
}): Promise<void> => {
  const endpoint = endpoints.seller.updateContactData.replace(
    ':sellerId',
    args.sellerId
  );
  await axiosV2.patch(endpoint, args.updateSellerContactDataRequest);
};

export const getSellerAddress = async (args: {
  sellerId: string;
}): Promise<SellerAddressResponse> => {
  const endpoint = endpoints.seller.getSellerAddress.replace(
    ':sellerId',
    args.sellerId
  );
  return await axiosV2.get<SellerAddressResponse>(endpoint);
};

export const updateSellerAddress = async (args: {
  sellerId: string;
  sellerAddressRequest: SellerAddressRequest;
}): Promise<void> => {
  const endpoint = endpoints.seller.updateSellerAddress.replace(
    ':sellerId',
    args.sellerId
  );
  await axiosV2.patch(endpoint, args.sellerAddressRequest);
};

export const getSellerFiscalData = async (args: {
  sellerId: string;
}): Promise<SellerFiscalDataResponse> => {
  const endpoint = endpoints.seller.fiscalData.replace(
    ':sellerId',
    args.sellerId
  );
  return await axiosV2.get<SellerFiscalDataResponse>(endpoint);
};

export const updateSellerFiscalData = async (args: {
  sellerId: string;
  updateSellerContactDataRequest: UpdateSellerFiscalDataRequest;
}): Promise<void> => {
  const endpoint = endpoints.seller.updateFiscalData.replace(
    ':sellerId',
    args.sellerId
  );
  await axiosV2.patch(endpoint, args.updateSellerContactDataRequest);
};

export const getSellerSettings = async (args: {
  sellerId: string;
}): Promise<SellerSettingsResponse> => {
  const endpoint = endpoints.seller.getSellerSettings.replace(
    ':sellerId',
    args.sellerId
  );
  return await axiosV2.get<SellerSettingsResponse>(endpoint);
};

export const updateSellerDeliveryDetails = async (args: {
  sellerId: string;
  updateSellerDeliveryDetails: UpdateSellerDeliveryDetailsRequest;
}) => {
  const endpoint = endpoints.seller.updateDeliveryDetails.replace(
    ':sellerId',
    args.sellerId
  );
  await axiosV2.post(endpoint, args.updateSellerDeliveryDetails);
};

export const updateSellerDeliverySchedules = async (args: {
  sellerId: string;
  updateSellerDeliverySchedulesRequest: UpdateSellerDeliverySchedulesRequest;
}) => {
  const endpoint = endpoints.seller.updateSellerSettings.replace(
    ':sellerId',
    args.sellerId
  );
  await axiosV2.post(endpoint, args.updateSellerDeliverySchedulesRequest);
};

export const getSellerDeliveryDays = async (): Promise<
  SellerDeliveryDayResponse[]
> => {
  const endpoint = endpoints.seller.getDeliveryDays;
  return await axiosV2.get<SellerDeliveryDayResponse[]>(endpoint);
};
